import * as msal from "@azure/msal-browser";
import { useMutation } from "@tanstack/react-query";
import { ControlCodes } from "@theprelude/core";
import { msAppId } from "./env";

const GRAPH_API_URL = "https://graph.microsoft.com/v1.0";

// Helper function to extract error message from response
async function getErrorMessageFromResponse(
  response: Response,
): Promise<string> {
  const errorText = await response.text();
  try {
    const errorJson = JSON.parse(errorText);
    if (errorJson?.error?.message) {
      return errorJson.error.message;
    }
  } catch {
    // Ignore parsing error, return raw text
  }
  return errorText;
}

const ResourceAppIds = {
  MicrosoftGraph: "00000003-0000-0000-c000-000000000000",
  Office365ExchangeOnline: "00000002-0000-0ff1-ce00-000000000000",
  WindowsDefenderATP: "fc780465-2017-40d4-a0c5-307022471b92",
};

const AppResources = {
  "User.Read": { id: "e1fe6dd8-ba31-4d61-89e7-88639da4683d", type: "Scope" },
  "ThreatHunting.Read.All": {
    id: "dd98c7f5-2d42-42d3-a0e4-633161547251",
    type: "Role",
  },
  "CustomDetection.ReadWrite.All": {
    id: "e0fd9c8d-a12e-4cc9-9827-20c8c3cd6fb8",
    type: "Role",
  },
  "DeviceManagementConfiguration.Read.All": {
    id: "dc377aa6-52d8-4e23-b271-2a7ae04cedf3",
    type: "Role",
  },
  "Device.Read.All": {
    id: "7438b122-aefc-4978-80ed-43db9fcc7715",
    type: "Role",
  },
  "DeviceManagementManagedDevices.Read.All": {
    id: "2f51be20-0bb4-4fed-bf7b-db946066c75e",
    type: "Role",
  },
  "Exchange.ManageAsApp": {
    id: "dc50a0fb-09a3-484d-be87-e023b12c6440",
    type: "Role",
  },
  "Alert.Read.All": {
    id: "71fe6b80-7034-4028-9ed8-0f316df9c3ff",
    type: "Role",
  },
  "Machine.Read.All": {
    id: "ea8291d3-4b9a-44b5-bc3a-6cea3026dc79",
    type: "Role",
  },
  "Ti.Read.All": {
    id: "528ca142-c849-4a5b-935e-10b8b9c38a84",
    type: "Role",
  },
  "AuditLog.Read.All": {
    id: "b0afded3-3588-46d8-8b3d-9842eff778da",
    type: "Role",
  },
  "Identity.RiskyUser.Read.All": {
    id: "dc5007c0-2d7d-4c42-879c-2dab87571379",
    type: "Role",
  },
  "Policy.Read.All": {
    id: "246dd0d5-5bd0-4def-940b-0421030a5b68",
    type: "Role",
  },
  "User.Read.All": {
    id: "df021288-bdef-4463-88db-98f22de89214",
    type: "Role",
  },
  "UserAuthenticationMethod.Read.All": {
    id: "38d9df27-64da-44fd-b7c5-a6fbac20248f",
    type: "Role",
  },
  "MailboxSettings.Read": {
    id: "40f97065-369a-49f4-947c-6a255697ae91",
    type: "Role",
  },
};

export interface ResourceAccess {
  id: string;
  type: string;
}

export interface Permission {
  resourceAppId: string;
  resourceAccess: ResourceAccess[];
}

export const mergePermissions = (
  ...permissionsArrays: Permission[][]
): Permission[] => {
  const mergedMap = new Map<
    string,
    {
      resourceAppId: string;
      resourceAccess: Map<string, { id: string; type: string }>;
    }
  >();

  // Process each permissions array
  permissionsArrays.forEach((permissionsArray) => {
    permissionsArray.forEach((permission) => {
      const { resourceAppId, resourceAccess } = permission;

      // If this resourceAppId doesn't exist in our map yet, add it
      if (!mergedMap.has(resourceAppId)) {
        mergedMap.set(resourceAppId, {
          resourceAppId,
          resourceAccess: new Map(),
        });
      }

      // Get the existing resource access map for this app ID
      const existingResourceAccess =
        mergedMap.get(resourceAppId)?.resourceAccess;

      // Add each resource access entry if it doesn't already exist
      resourceAccess.forEach((access) => {
        if (existingResourceAccess && !existingResourceAccess.has(access.id)) {
          existingResourceAccess.set(access.id, access);
        }
      });
    });
  });

  // Convert the map back to the expected array format
  return [...mergedMap.values()].map((entry) => ({
    resourceAppId: entry.resourceAppId,
    resourceAccess: [...entry.resourceAccess.values()],
  }));
};

const DefenderPermissions = [
  {
    resourceAppId: ResourceAppIds.MicrosoftGraph,
    resourceAccess: [
      AppResources["User.Read"],
      AppResources["ThreatHunting.Read.All"],
      AppResources["CustomDetection.ReadWrite.All"],
      AppResources["DeviceManagementConfiguration.Read.All"],
    ],
  },
  {
    resourceAppId: ResourceAppIds.WindowsDefenderATP,
    resourceAccess: [
      AppResources["Machine.Read.All"],
      AppResources["Ti.Read.All"],
      AppResources["Alert.Read.All"],
    ],
  },
];

const IntunePermissions = [
  {
    resourceAppId: ResourceAppIds.MicrosoftGraph,
    resourceAccess: [
      AppResources["User.Read"],
      AppResources["Device.Read.All"],
      AppResources["DeviceManagementManagedDevices.Read.All"],
      AppResources["DeviceManagementConfiguration.Read.All"],
    ],
  },
];

const M365Permissions = [
  {
    resourceAppId: ResourceAppIds.MicrosoftGraph,
    resourceAccess: [
      AppResources["User.Read"],
      AppResources["MailboxSettings.Read"],
    ],
  },
  {
    resourceAppId: ResourceAppIds.Office365ExchangeOnline,
    resourceAccess: [AppResources["Exchange.ManageAsApp"]],
  },
];

const EntraIDPermissions = [
  {
    resourceAppId: ResourceAppIds.MicrosoftGraph,
    resourceAccess: [
      AppResources["User.Read.All"],
      AppResources["DeviceManagementConfiguration.Read.All"],
      AppResources["AuditLog.Read.All"],
      AppResources["Identity.RiskyUser.Read.All"],
      AppResources["Policy.Read.All"],
      AppResources["UserAuthenticationMethod.Read.All"],
    ],
  },
];

async function getTenantId(token: string) {
  const url = `${GRAPH_API_URL}/organization`;

  const response = await fetch(url, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });

  if (response.ok) {
    const result = await response.json();
    return result.value[0].id as string;
  } else {
    const errorMessage = await getErrorMessageFromResponse(response);
    throw new Error(`Failed to retrieve tenant ID: ${errorMessage}`);
  }
}

async function createClientSecret(token: string, appId: string) {
  const url = `${GRAPH_API_URL}/applications?$filter=appId eq '${appId}'`;

  // Get the object ID of the App Registration
  const response = await fetch(url, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    const errorMessage = await getErrorMessageFromResponse(response);
    throw new Error(`Failed to get App Object ID: ${errorMessage}`);
  }

  const data = await response.json();
  const appObjectId = data.value[0].id; // Application Object ID

  // Create a new Client Secret
  const secretUrl = `${GRAPH_API_URL}/applications/${appObjectId}/addPassword`;
  const secretData = {
    displayName: "PreludeSCMIntegration",
    endDateTime: new Date(
      new Date().setFullYear(new Date().getFullYear() + 1),
    ).toISOString(), // 1-year expiry
  };

  const secretResponse = await fetch(secretUrl, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(secretData),
  });

  if (secretResponse.ok) {
    const secretResult = await secretResponse.json();
    return secretResult.secretText as string;
  } else {
    const errorMessage = await getErrorMessageFromResponse(secretResponse);
    throw new Error(`Failed to create Client Secret: ${errorMessage}`);
  }
}

async function getServicePrincipal(token: string, appId: string) {
  const url = `${GRAPH_API_URL}/servicePrincipals(appId='${appId}')`;

  const response = await fetch(url, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    const errorMessage = await getErrorMessageFromResponse(response);
    throw new Error(`Failed to get Service Principal: ${errorMessage}`);
  }

  return (await response.json()) as {
    id: string;
    appId: string;
    displayName: string;
  };
}

const getAppRoleAssignments = async (token: string, principalId: string) => {
  const url = `${GRAPH_API_URL}/servicePrincipals/${principalId}/appRoleAssignments`;

  const response = await fetch(url, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    const errorMessage = await getErrorMessageFromResponse(response);
    throw new Error(`Failed to get App Role Assignments: ${errorMessage}`);
  }

  return (await response.json()) as {
    value: {
      id: string;
    }[];
  };
};

async function grantAdminConsent(
  token: string,
  principalId: string,
  resourceId: string,
  appRoleId: string,
) {
  const consentUrl = `${GRAPH_API_URL}/servicePrincipals/${principalId}/appRoleAssignments`;

  const consentResponse = await fetch(consentUrl, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      principalId,
      resourceId,
      appRoleId,
    }),
  });

  if (consentResponse.ok) {
    return true;
  } else {
    const errorMessage = await getErrorMessageFromResponse(consentResponse);
    throw new Error(`Failed to grant admin consent: ${errorMessage}`);
  }
}

export async function assignGlobalReaderRole(
  token: string,
  servicePrincipalId: string,
) {
  // First, get the Security Reader role definition
  const roleDefinitionsUrl = `${GRAPH_API_URL}/roleManagement/directory/roleDefinitions?$filter=displayName eq 'Global Reader'`;

  const roleDefinitionsResponse = await fetch(roleDefinitionsUrl, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });

  if (!roleDefinitionsResponse.ok) {
    const errorMessage = await getErrorMessageFromResponse(
      roleDefinitionsResponse,
    );
    throw new Error(`Failed to get Global Reader role: ${errorMessage}`);
  }

  const roleDefinitions = await roleDefinitionsResponse.json();
  const globalReaderRoleId = roleDefinitions.value[0]?.id;

  if (!globalReaderRoleId) {
    throw new Error("Global Reader role not found");
  }

  // Check if the role is already assigned
  const existingRoleAssignmentsUrl = `${GRAPH_API_URL}/roleManagement/directory/roleAssignments?$filter=principalId eq '${servicePrincipalId}' and roleDefinitionId eq '${globalReaderRoleId}'`;

  const existingRoleResponse = await fetch(existingRoleAssignmentsUrl, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });

  if (!existingRoleResponse.ok) {
    const errorMessage =
      await getErrorMessageFromResponse(existingRoleResponse);
    throw new Error(
      `Failed to check existing role assignments: ${errorMessage}`,
    );
  }

  const existingRoles = await existingRoleResponse.json();

  // Only proceed with assignment if the role isn't already assigned
  if (existingRoles.value.length > 0) {
    return true; // Role is already assigned
  }

  // Assign Security Reader Role
  const roleAssignmentUrl = `${GRAPH_API_URL}/roleManagement/directory/roleAssignments`;

  const roleAssignment = {
    principalId: servicePrincipalId,
    roleDefinitionId: globalReaderRoleId,
    directoryScopeId: "/",
  };

  const roleResponse = await fetch(roleAssignmentUrl, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(roleAssignment),
  });

  if (roleResponse.ok) {
    return true;
  } else {
    const errorMessage = await getErrorMessageFromResponse(roleResponse);
    throw new Error(`Failed to assign Global Reader role: ${errorMessage}`);
  }
}

async function createAppRegistration({
  token,
  controls,
  displayName,
}: {
  token: string;
  controls: SupportedMicrosoftControl[];
  displayName: string;
}) {
  const url = `${GRAPH_API_URL}/applications`;

  const appData = {
    displayName,
    signInAudience: "AzureADMyOrg",
    requiredResourceAccess: mergePermissions(
      ...controls.map((control) => ControlRequiredResourceAccess[control]),
    ),
  };

  const response = await fetch(url, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(appData),
  });

  if (response.ok) {
    const result = await response.json();
    return result as {
      id: string;
      appId: string;
      displayName: string;
      requiredResourceAccess: Permission[];
    };
  } else {
    const errorMessage = await getErrorMessageFromResponse(response);
    throw new Error(`Failed to create app: ${errorMessage}`);
  }
}

async function updateAppRegistration(
  token: string,
  appObjectId: string,
  existingAppRegistration: {
    requiredResourceAccess: Permission[];
  },
  controls: SupportedMicrosoftControl[],
) {
  const url = `${GRAPH_API_URL}/myorganization/applications/${appObjectId}`;
  const permissions = mergePermissions(
    existingAppRegistration.requiredResourceAccess,
    ...controls.map((control) => ControlRequiredResourceAccess[control]),
  );

  const response = await fetch(url, {
    method: "PATCH",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      requiredResourceAccess: permissions,
    }),
  });

  if (response.ok) {
    return true;
  } else {
    const errorMessage = await getErrorMessageFromResponse(response);
    throw new Error(`Failed to update app registration: ${errorMessage}`);
  }
}

async function createServicePrincipal(token: string, appId: string) {
  const url = `${GRAPH_API_URL}/servicePrincipals`;

  const response = await fetch(url, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      appId,
    }),
  });

  if (response.ok) {
    return (await response.json()) as {
      id: string;
      appId: string;
      displayName: string;
    };
  } else {
    const errorMessage = await getErrorMessageFromResponse(response);
    throw new Error(`Failed to create service principal: ${errorMessage}`);
  }
}

const CommonScopes = [
  "User.Read",
  "Application.ReadWrite.All",
  "AppRoleAssignment.ReadWrite.All",
];

const M365Scopes = [...CommonScopes, "RoleManagement.ReadWrite.Directory"];

async function loginAndCreateToken({ scopes }: { scopes: string[] }) {
  const msalConfig = {
    auth: {
      clientId: msAppId,
      authority: "https://login.microsoftonline.com/common",
      // We use the verified route to avoid redirecting to the home page
      // This causes where the login fails because a hash is removed from the redirect uri
      redirectUri: `${window.location.origin}/verified`,
    },
  };
  const loginRequest = {
    scopes,
  };
  const msalInstance = new msal.PublicClientApplication(msalConfig);
  await msalInstance.initialize();
  const loginResponse = await msalInstance.loginPopup({
    scopes,
    prompt: "login",
  });

  msalInstance.setActiveAccount(loginResponse.account);
  return await msalInstance.acquireTokenSilent(loginRequest);
}

const getAppRegistration = async (token: string, displayName: string) => {
  const url = `${GRAPH_API_URL}/applications?$filter=displayName eq '${displayName}'`;

  const response = await fetch(url, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    const errorMessage = await getErrorMessageFromResponse(response);
    throw new Error(`Failed to get app registration: ${errorMessage}`);
  }

  return (await response.json()) as {
    value: {
      id: string;
      appId: string;
      displayName: string;
      requiredResourceAccess: Permission[];
    }[];
  };
};

/**
 * NOTE: `DEFENDER_DISCOVERY` is intentionally excluded here due to how it is
 * handled on the back-end, where it is grouped alongside `DEFENDER`.
 */
const ControlRequiredResourceAccess = {
  [ControlCodes.DEFENDER]: DefenderPermissions,
  [ControlCodes.INTUNE]: IntunePermissions,
  [ControlCodes.M365]: M365Permissions,
  [ControlCodes.ENTRA]: EntraIDPermissions,
} as const;

export type SupportedMicrosoftControl =
  keyof typeof ControlRequiredResourceAccess;

async function grantAllRequiredPermissions(
  token: string,
  appServicePrincipalId: string,
  controlRequiredResourceAccess: (typeof ControlRequiredResourceAccess)[keyof typeof ControlRequiredResourceAccess],
) {
  const appRoleAssignments = await getAppRoleAssignments(
    token,
    appServicePrincipalId,
  );

  for (const resource of controlRequiredResourceAccess) {
    const resourceServicePrincipal = await getServicePrincipal(
      token,
      resource.resourceAppId,
    );

    for (const permission of resource.resourceAccess) {
      // Skip User.Read permission as it doesn't need admin consent
      // A better way to do this would be to check if the permission is already granted or not needed
      // This is a temporary solution
      if (permission.id === AppResources["User.Read"].id) {
        continue;
      }

      const existingPermission = appRoleAssignments.value.find(
        (assignment: any) =>
          assignment.appRoleId === permission.id &&
          assignment.resourceId === resourceServicePrincipal.id,
      );

      if (existingPermission) {
        continue;
      }

      try {
        await grantAdminConsent(
          token,
          appServicePrincipalId,
          resourceServicePrincipal.id,
          permission.id,
        );
      } catch (error) {
        throw new Error(
          `Failed to grant permission ${permission.id}: ${error}`,
        );
      }
    }
  }
}

export const useConnectIntegrationViaAzure = () => {
  return useMutation({
    mutationFn: async ({
      controls,
      accountId,
    }: {
      controls: SupportedMicrosoftControl[];
      accountId: string;
    }) => {
      let requiredAdminConsent = false;
      let requiredGlobalReaderRoleAssignment = false;
      const scopes = controls.includes(ControlCodes.M365)
        ? M365Scopes
        : CommonScopes;

      const tokenResponse = await loginAndCreateToken({ scopes });
      const displayName = `PreludeSCMIntegration:${accountId}`;
      const controlRequiredResourceAccess = mergePermissions(
        ...controls.map((control) => ControlRequiredResourceAccess[control]),
      );

      const appRegistrations = await getAppRegistration(
        tokenResponse.accessToken,
        displayName,
      );

      let appRegistration = appRegistrations.value[0];

      if (appRegistration) {
        await updateAppRegistration(
          tokenResponse.accessToken,
          appRegistration.id,
          {
            requiredResourceAccess: appRegistration.requiredResourceAccess,
          },
          controls,
        );
      } else {
        appRegistration = await createAppRegistration({
          token: tokenResponse.accessToken,
          controls,
          displayName,
        });

        await createServicePrincipal(
          tokenResponse.accessToken,
          appRegistration.appId,
        );
      }

      const appServicePrincipal = await getServicePrincipal(
        tokenResponse.accessToken,
        appRegistration.appId,
      );

      const appServicePrincipalId = appServicePrincipal.id;

      if (
        controls.includes(ControlCodes.M365) ||
        controls.includes(ControlCodes.ENTRA)
      ) {
        try {
          await assignGlobalReaderRole(
            tokenResponse.accessToken,
            appServicePrincipalId,
          );
        } catch {
          requiredGlobalReaderRoleAssignment = true;
        }
      }
      try {
        await grantAllRequiredPermissions(
          tokenResponse.accessToken,
          appServicePrincipalId,
          controlRequiredResourceAccess,
        );
      } catch {
        requiredAdminConsent = true;
      }

      const tenantId = await getTenantId(tokenResponse.accessToken);

      const clientSecret = await createClientSecret(
        tokenResponse.accessToken,
        appRegistration.appId,
      );

      return {
        appId: appRegistration.appId as string,
        tenantId,
        clientSecret,
        requiredAdminConsent,
        requiredGlobalReaderRoleAssignment,
      };
    },
  });
};
